import { chatApi } from './chatApi';

class GroupLockService {
  constructor() {
    this.checkInterval = null;
    this.lockedGroups = new Set();
  }

  // Start monitoring group locks
  startMonitoring() {
    if (this.checkInterval) return;
    
    this.checkInterval = setInterval(() => {
      this.checkGroupLocks();
    }, 60000); // Check every minute
  }

  // Stop monitoring
  stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Check if groups should be locked
  async checkGroupLocks() {
    try {
      const conversations = await chatApi.getConversations();
      const groupsToCheck = conversations.filter(conv => 
        conv.isGroup && 
        conv.autoLockEnabled && 
        !this.lockedGroups.has(conv.id)
      );

      for (const group of groupsToCheck) {
        if (this.shouldLockGroup(group)) {
          await this.lockGroup(group.id);
        }
      }
    } catch (error) {
      console.error('Error checking group locks:', error);
    }
  }

  // Check if a group should be locked
  shouldLockGroup(group) {
    if (!group.autoLockEnabled || !group.lastMessageAt) return false;
    
    const lastActivity = new Date(group.lastMessageAt);
    const lockTime = new Date(lastActivity.getTime() + (group.lockDuration * 60 * 60 * 1000));
    return new Date() > lockTime;
  }

  // Lock a group
  async lockGroup(groupId) {
    try {
      await chatApi.updateGroup(groupId, {
        lockedAt: new Date().toISOString()
      });
      
      this.lockedGroups.add(groupId);
      
      // Send system message about lock
      await chatApi.sendMessage(
        groupId,
        'system',
        'group',
        '🔒 This group has been locked due to inactivity. Contact an admin to unlock.',
        'system'
      );
      
      console.log(`Group ${groupId} has been locked`);
    } catch (error) {
      console.error('Error locking group:', error);
    }
  }

  // Unlock a group (admin only)
  async unlockGroup(groupId, userId) {
    try {
      // Check if user is admin
      const participants = await chatApi.getGroupParticipants(groupId);
      const userParticipant = participants.find(p => p.userID === userId);
      
      if (!userParticipant || userParticipant.role !== 'ADMIN') {
        throw new Error('Only admins can unlock groups');
      }
      
      await chatApi.updateGroup(groupId, {
        lockedAt: null
      });
      
      this.lockedGroups.delete(groupId);
      
      // Send system message about unlock
      await chatApi.sendMessage(
        groupId,
        'system',
        'group',
        '🔓 This group has been unlocked by an admin.',
        'system'
      );
      
      console.log(`Group ${groupId} has been unlocked`);
    } catch (error) {
      console.error('Error unlocking group:', error);
      throw error;
    }
  }

  // Check if a group is locked
  isGroupLocked(group) {
    if (!group.autoLockEnabled) return false;
    return !!group.lockedAt || this.lockedGroups.has(group.id);
  }

  // Get lock status message
  getLockStatusMessage(group) {
    if (!this.isGroupLocked(group)) return null;
    
    const lockTime = new Date(group.lockedAt);
    return `This group was locked on ${lockTime.toLocaleDateString()} due to inactivity.`;
  }

  // Reset group activity (when new message is sent)
  resetGroupActivity(groupId) {
    this.lockedGroups.delete(groupId);
  }
}

export const groupLockService = new GroupLockService();